import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Building2, ArrowRight, Sparkles, Target, RefreshCw, X, Check, Download } from 'lucide-react';
import Live2DAvatarGenerated, { Live2DAvatarGeneratedRef } from '../components/Live2DAvatarGenerated';

interface FormData {
  name: string;
  purpose: string;
  companyUrl: string;
}

interface CompanyData {
  title: string;
  content: string;
  url: string;
  timestamp: string;
  businessType: string;
  confidence?: number;
  companyDescription: string;
  digitalHumanPersonality: string;
}

// 动态加载脚本的函数
function loadScript(url: string) {
  return new Promise<void>((resolve, reject) => {
    // 检查脚本是否已经存在
    const existingScript = document.getElementById('avatar-anime-script');
    if (existingScript) {
      console.log('Script already loaded, skipping...');
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'avatar-anime-script';
    script.src = url;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// 按顺序加载所需的脚本
async function loadDependencies(url: string) {
  const scripts = [url];

  for (const script of scripts) {
    await loadScript(script);
  }
}

const Page1: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(() => {
    // 从localStorage恢复表单数据
    const savedFormData = localStorage.getItem('avatarFormData');
    if (savedFormData) {
      try {
        return JSON.parse(savedFormData);
      } catch (error) {
        console.log('Failed to parse saved form data:', error);
      }
    }
    return {
      name: '',
      purpose: '',
      companyUrl: '',
    };
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAvatar, setGeneratedAvatar] = useState<string | null>(null);
  const [generatedClothingUrl, setGeneratedClothingUrl] = useState<string | null>(null);
  const [live2dLoaded, setLive2dLoaded] = useState(false);
  const [live2dError, setLive2dError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [avatarUUID1, setAvatarUUID1] = useState<string | null>(null);
  const [avatarUUID2, setAvatarUUID2] = useState<string | null>(null);
  const [textureUrl1, setTextureUrl1] = useState<string | null>(null);
  const [textureUrl2, setTextureUrl2] = useState<string | null>(null);
  const [modelUrl1, setModelUrl1] = useState<string | null>(null);
  const [modelUrl2, setModelUrl2] = useState<string | null>(null);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const live2dRef = useRef<Live2DAvatarGeneratedRef>(null);
  const live2dRef2 = useRef<Live2DAvatarGeneratedRef>(null);


  // 原生表单验证函数
  const validateForm = (formElement: HTMLFormElement): boolean => {
    // 使用原生HTML5验证
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return false;
    }
    return true;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    // 保存到localStorage
    localStorage.setItem('avatarFormData', JSON.stringify(newFormData));
  };

  const fetchLatestGeneratedClothing = useCallback(async () => {
    try {
      const response = await fetch('/api/latest-generated-clothing');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.url) {
          setGeneratedClothingUrl(`/api${data.url}`);
          console.log('Found generated clothing:', data.filename);
        } else {
          console.log('No generated clothing found:', data.message);
          setGeneratedClothingUrl(null);
        }
      } else {
        console.error('Failed to fetch clothing info, status:', response.status);
        setGeneratedClothingUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch latest generated clothing:', error);
      setGeneratedClothingUrl(null);
    }
  }, []);

  const updateHaruGeneratedClothing = useCallback(async () => {
    try {
      const response = await fetch('/api/update-haru-generated-clothing', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Haru generated clothing updated successfully:', data.message);
        } else {
          console.log('Haru update info:', data.message);
        }
      } else {
        console.error('Failed to update haru clothing, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to update haru generated clothing:', error);
    }
  }, []);


  const handleApplyGeneratedClothing = async () => {
    if (!generatedClothingUrl) {
      return;
    }
    
    try {
      const response1 = await fetch('/api/apply-generated-clothing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const response2 = await fetch('/api/update-haru-generated-clothing', {
        method: 'POST'
      });
      
      if (response1.ok && response2.ok) {
        // 等待足够时间让文件更新完成
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // 刷新两个Live2D模型
        if (live2dRef.current && live2dRef.current.refreshModel) {
          await live2dRef.current.refreshModel();
        }
        if (live2dRef2.current && live2dRef2.current.refreshModel) {
          await live2dRef2.current.refreshModel();
        }
        setSuccessMessage('生成された服装が正常に適用されました！Live2Dモデルとtexture_01.pngの両方が更新されました。');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setSuccessMessage('服装の適用に失敗しました。');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Failed to apply generated clothing:', error);
      setSuccessMessage('服装の適用中にエラーが発生しました。');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  // 生成UUID的函数
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // 截图并上传到S3
  const captureAndUploadScreenshot = async (avatarUUID: string, windowNumber: number) => {
    try {
      console.log(`Starting screenshot capture for window ${windowNumber}, UUID: ${avatarUUID}`);

      // 通过live2dRef直接获取对应的canvas
      let canvas: HTMLCanvasElement | null = null;

      if (windowNumber === 1 && live2dRef.current) {
        // 获取第一个Live2D组件的canvas
        const canvasElements = document.querySelectorAll('canvas.live2d-canvas');
        for (let i = 0; i < canvasElements.length; i++) {
          const canvasElement = canvasElements[i] as HTMLCanvasElement;
          // 通过检查canvas的父级容器来识别第一个Live2D实例
          const container = canvasElement.closest('.grid.grid-cols-2');
          if (container) {
            const firstChild = container.querySelector('.space-y-4:first-child canvas.live2d-canvas');
            if (firstChild === canvasElement) {
              canvas = canvasElement;
              break;
            }
          }
        }
      } else if (windowNumber === 2 && live2dRef2.current) {
        // 获取第二个Live2D组件的canvas
        const canvasElements = document.querySelectorAll('canvas.live2d-canvas');
        for (let i = 0; i < canvasElements.length; i++) {
          const canvasElement = canvasElements[i] as HTMLCanvasElement;
          // 通过检查canvas的父级容器来识别第二个Live2D实例
          const container = canvasElement.closest('.grid.grid-cols-2');
          if (container) {
            const lastChild = container.querySelector('.space-y-4:last-child canvas.live2d-canvas');
            if (lastChild === canvasElement) {
              canvas = canvasElement;
              break;
            }
          }
        }
      }

      // 如果上述方法失败，回退到按顺序选择canvas
      if (!canvas) {
        console.log('Fallback to canvas selection by index');
        const allCanvases = document.querySelectorAll('canvas.live2d-canvas');
        console.log(`Found ${allCanvases.length} live2d canvas elements`);

        if (allCanvases.length >= windowNumber) {
          canvas = allCanvases[windowNumber - 1] as HTMLCanvasElement;
        } else if (allCanvases.length > 0) {
          canvas = allCanvases[0] as HTMLCanvasElement;
        }
      }

      if (!canvas) {
        throw new Error(`No canvas found for window ${windowNumber}`);
      }

      console.log(`Canvas found for window ${windowNumber}:`, canvas.id || 'no-id');

      // 等待Live2D模型完全加载和渲染
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 尝试多种截图方法
      let screenshotDataURL: string | null = null;

      try {
        // 方法1: 直接从canvas截图
        screenshotDataURL = canvas.toDataURL('image/png', 1.0);

        if (screenshotDataURL === 'data:,' || screenshotDataURL.length < 1000) {
          throw new Error('Canvas is empty or corrupted');
        }
      } catch (error) {
        console.warn('Direct canvas capture failed, trying WebGL context:', error);

        // 方法2: 通过WebGL上下文强制渲染后截图
        try {
          const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
          if (gl) {
            gl.finish(); // 确保所有WebGL命令完成
            await new Promise(resolve => setTimeout(resolve, 100));
            screenshotDataURL = canvas.toDataURL('image/png', 1.0);
          }
        } catch (glError) {
          console.warn('WebGL context capture failed:', glError);
        }
      }

      if (!screenshotDataURL || screenshotDataURL === 'data:,' || screenshotDataURL.length < 1000) {
        // 方法3: 截图整个容器区域（备选方案）
        console.warn('Canvas capture failed, trying container screenshot');
        try {
          const containerElement = canvas.closest('.bg-gradient-to-br') as HTMLElement;
          if (containerElement) {
            // 使用浏览器原生API截图整个容器
            const rect = containerElement.getBoundingClientRect();
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = rect.width;
            tempCanvas.height = rect.height;
            const ctx = tempCanvas.getContext('2d');

            if (ctx) {
              // 创建一个简单的截图，显示容器内容
              ctx.fillStyle = '#f8fafc';
              ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
              ctx.fillStyle = '#64748b';
              ctx.font = '16px Arial';
              ctx.textAlign = 'center';
              ctx.fillText('Live2D Avatar Screenshot', tempCanvas.width / 2, tempCanvas.height / 2);

              screenshotDataURL = tempCanvas.toDataURL('image/png', 1.0);
            }
          }
        } catch (containerError) {
          console.warn('Container screenshot failed:', containerError);
        }
      }

      if (!screenshotDataURL || screenshotDataURL === 'data:,' || screenshotDataURL.length < 1000) {
        throw new Error('All screenshot methods failed');
      }

      console.log(`Screenshot captured, data URL length: ${screenshotDataURL.length}`);

      // 上传截图到后端
      const uploadResponse = await fetch('/api/upload-screenshot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          screenshot_data: screenshotDataURL,
          avatar_name: formData.name,
          avatar_uuid: avatarUUID
        })
      });

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        console.log('Screenshot uploaded successfully:', result.screenshot_url);
        return result.screenshot_url;
      } else {
        const errorText = await uploadResponse.text();
        throw new Error(`Screenshot upload failed: ${uploadResponse.status} - ${errorText}`);
      }

    } catch (error) {
      console.error('Error in captureAndUploadScreenshot:', error);
      throw error;
    }
  };

  // 点击デジタルヒューマンを使用时使用已生成的UUID并保存数据，然后跳转到cs.gbase.ai
  const handleUseAvatar = async (avatarUUID: string | null, windowNumber: number) => {
    // 防止重复点击
    if (isSavingAvatar) {
      return;
    }

    // 检查是否已有UUID
    if (!avatarUUID) {
      console.error(`No UUID found for window ${windowNumber}, avatar generation may not have completed`);
      setSuccessMessage('エラー: アバターのUUIDが見つかりません。再生成してください。');
      setTimeout(() => setSuccessMessage(null), 5000);
      return;
    }
    
    setIsSavingAvatar(true);
    
    try {
      // 保存avatar数据到后端，供cs.gbase.ai后续查询
      const avatarData = {
        uuid: avatarUUID,
        name: formData.name,
        purpose: formData.purpose || '',
        company_url: formData.companyUrl || ''
      };
      
      const response = await fetch('/api/save-avatar-with-uuid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(avatarData)
      });
      
      if (response.ok) {
        console.log(`Avatar data saved for window ${windowNumber} with UUID:`, avatarUUID);

        // 异步执行截图上传，不阻塞保存流程
        captureAndUploadScreenshot(avatarUUID, windowNumber).catch(screenshotError => {
          console.error('Screenshot upload failed:', screenshotError);
        });
      } else {
        console.error(`Failed to save avatar data for window ${windowNumber}, but continuing...`);
      }

      // 保存成功，立即跳转到gbase
      window.open(`https://admin.gbase.ai/auto-setup?uuid=${avatarUUID}`, '_blank');
      
    } catch (error) {
      console.error('Error saving avatar data:', error);
      // 即使发生错误也要跳转，使用现有的UUID
      window.open(`https://admin.gbase.ai/auto-setup?uuid=${avatarUUID}`, '_blank');
    } finally {
      // 2秒后重置状态，防止用户需要再次点击
      setTimeout(() => {
        setIsSavingAvatar(false);
      }, 2000);
    }
  };

  // 再生成按钮点击事件 - 临时改为刷新网页
  const handleRegenerate = () => {
    // 先保存当前表单数据到localStorage
    try {
      localStorage.setItem('avatarFormData', JSON.stringify(formData));
    } catch {}

    // 临时改为刷新网页
    window.location.reload();

    // 以下是原来的再生成逻辑，临时注释掉
    /*
    // 原生表单验证
    const formElement = document.querySelector('form') as HTMLFormElement;
    if (formElement && !validateForm(formElement)) {
      return;
    }

    // 先保存当前表单，防止任何重渲染导致值丢失
    try {
      localStorage.setItem('avatarFormData', JSON.stringify(formData));
    } catch {}
    setIsGenerating(true);
    setGeneratedClothingUrl(null);
    setLive2dLoaded(false);
    setLive2dError(null);

    try {
      console.log('Starting regeneration with:', formData);

      // 生成新的UUID用于再生成
      const newUuid1 = generateUUID();
      const newUuid2 = generateUUID();
      setAvatarUUID1(newUuid1);
      setAvatarUUID2(newUuid2);
      console.log('Generated new UUID1 for regeneration:', newUuid1);
      console.log('Generated new UUID2 for regeneration:', newUuid2);

      // 创建两个再生成请求的FormData
      const formDataToSend1 = new FormData();
      formDataToSend1.append('name', formData.name);
      formDataToSend1.append('uuid', newUuid1);
      formDataToSend1.append('avatar_index', '1');
      if (formData.purpose) {
        formDataToSend1.append('purpose', formData.purpose);
      }
      if (formData.companyUrl) {
        formDataToSend1.append('company_url', formData.companyUrl);
      }

      const formDataToSend2 = new FormData();
      formDataToSend2.append('name', formData.name);
      formDataToSend2.append('uuid', newUuid2);
      formDataToSend2.append('avatar_index', '2');
      if (formData.purpose) {
        formDataToSend2.append('purpose', formData.purpose);
      }
      if (formData.companyUrl) {
        formDataToSend2.append('company_url', formData.companyUrl);
      }

      console.log('Sending two simultaneous regeneration requests...');

      // 同时发送两个再生成请求
      const [response1, response2] = await Promise.all([
        fetch('/api/generate-avatar', {
          method: 'POST',
          body: formDataToSend1
        }),
        fetch('/api/generate-avatar', {
          method: 'POST',
          body: formDataToSend2
        })
      ]);

      if (response1.ok && response2.ok) {
        const [result1, result2] = await Promise.all([
          response1.json(),
          response2.json()
        ]);

        console.log('Regeneration backend response 1:', result1);
        console.log('Regeneration backend response 2:', result2);

        // 等待足够时间让后端文件完全写入和复制完成
        await new Promise(resolve => setTimeout(resolve, 1000));

        // 刷新两个Live2D模型
        if (live2dRef.current && live2dRef.current.refreshModel) {
          console.log('Calling refreshModel for first preview...');
          await live2dRef.current.refreshModel();
          console.log('refreshModel completed for first preview');
        }

        if (live2dRef2.current && live2dRef2.current.refreshModel) {
          console.log('Calling refreshModel for second preview...');
          await live2dRef2.current.refreshModel();
          console.log('refreshModel completed for second preview');
        }

        // 再生成完成后，恢复输入框显示为本地缓存的值
        try {
          const saved = localStorage.getItem('avatarFormData');
          if (saved) {
            setFormData(JSON.parse(saved));
          }
        } catch {}
      } else {
        let errorMessage = '再生成失败：';

        if (!response1.ok) {
          const errorData1 = await response1.json();
          errorMessage += `Avatar1: ${errorData1.detail || '未知错误'} `;
        }

        if (!response2.ok) {
          const errorData2 = await response2.json();
          errorMessage += `Avatar2: ${errorData2.detail || '未知错误'}`;
        }

        console.error('Regeneration API Error:', errorMessage);
        setSuccessMessage(errorMessage);
        setTimeout(() => setSuccessMessage(null), 5000);
      }

    } catch (error) {
      console.error('Failed to regenerate:', error);
      setSuccessMessage('再生成プロセス中にエラーが発生しました。再試行してください。');
      setTimeout(() => setSuccessMessage(null), 5000);
    } finally {
      setIsGenerating(false);
    }
    */
  };

  const handleBackToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreview(false);
    setGeneratedAvatar(null);
    setLive2dLoaded(false);
    setLive2dError(null);
    // 注意：不清除localStorage中的表单数据，保持用户输入
    // 刷新当前网页
    window.location.reload();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(e.currentTarget as HTMLFormElement)) {
      return;
    }

    setIsAnalyzing(true);
    setGeneratedAvatar(null);
    setGeneratedClothingUrl(null);
    setLive2dLoaded(false);
    setLive2dError(null);
    setTextureUrl1(null);
    setTextureUrl2(null);
    setModelUrl1(null);
    setModelUrl2(null);

    try {
      console.log('Calling backend API with:', formData);

      // 生成两个UUID，分别对应两个窗口
      const uuid1 = generateUUID();
      const uuid2 = generateUUID();
      setAvatarUUID1(uuid1);
      setAvatarUUID2(uuid2);
      console.log('Generated UUID1 for window 1:', uuid1);
      console.log('Generated UUID2 for window 2:', uuid2);

      // 创建两个请求的FormData
      const formDataToSend1 = new FormData();
      formDataToSend1.append('name', formData.name);
      formDataToSend1.append('uuid', uuid1);
      formDataToSend1.append('avatar_index', '1');
      if (formData.purpose) {
        formDataToSend1.append('purpose', formData.purpose);
      }
      if (formData.companyUrl) {
        formDataToSend1.append('company_url', formData.companyUrl);
      }

      const formDataToSend2 = new FormData();
      formDataToSend2.append('name', formData.name);
      formDataToSend2.append('uuid', uuid2);
      formDataToSend2.append('avatar_index', '2');
      if (formData.purpose) {
        formDataToSend2.append('purpose', formData.purpose);
      }
      if (formData.companyUrl) {
        formDataToSend2.append('company_url', formData.companyUrl);
      }

      console.log('Sending two simultaneous generation requests...');

      // 同时发送两个生成请求
      const [response1, response2] = await Promise.all([
        fetch('/api/generate-avatar', {
          method: 'POST',
          body: formDataToSend1
        }),
        fetch('/api/generate-avatar', {
          method: 'POST',
          body: formDataToSend2
        })
      ]);

      console.log('Both requests completed, processing responses...');

      // 处理两个响应
      if (response1.ok && response2.ok) {
        const [result1, result2] = await Promise.all([
          response1.json(),
          response2.json()
        ]);

        console.log('Backend response 1:', result1);
        console.log('Backend response 2:', result2);

        setAvatarUUID1(result1.avatar_uuid || uuid1);
        setAvatarUUID2(result2.avatar_uuid || uuid2);
        setTextureUrl1(result1.texture_path || null);
        setTextureUrl2(result2.texture_path || null);
        setModelUrl1(result1.model_url || null);
        setModelUrl2(result2.model_url || null);

        setIsAnalyzing(false);
        setShowPreview(true);
        setGeneratedAvatar('generated');

        console.log('Both avatars generated successfully');

        // 等待一段时间让后端文件完成复制
        await new Promise(resolve => setTimeout(resolve, 1000));

        // 刷新两个Live2D模型
        if (live2dRef.current && live2dRef.current.refreshModel) {
          console.log('Refreshing first Live2D model...');
          await live2dRef.current.refreshModel();
          console.log('First model refreshed');
        }

        if (live2dRef2.current && live2dRef2.current.refreshModel) {
          console.log('Refreshing second Live2D model...');
          await live2dRef2.current.refreshModel();
          console.log('Second model refreshed');
        }

        console.log('Avatar generation completed successfully');

      } else {
        // 处理错误响应
        let errorMessage = '生成失败：';

        if (!response1.ok) {
          const errorData1 = await response1.json();
          errorMessage += `Avatar1: ${errorData1.detail || '未知错误'} `;
        }

        if (!response2.ok) {
          const errorData2 = await response2.json();
          errorMessage += `Avatar2: ${errorData2.detail || '未知错误'}`;
        }

        console.error('API Error:', errorMessage);
        setSuccessMessage(errorMessage);
        setTimeout(() => setSuccessMessage(null), 5000);
        setIsAnalyzing(false);
      }

    } catch (error) {
      console.error('Failed to generate avatars:', error);
      setSuccessMessage('生成过程中发生错误，请重试');
      setTimeout(() => setSuccessMessage(null), 5000);
      setIsAnalyzing(false);
    }
  };


  // 不再在页面加载时自动恢复状态，保持初始的表单状态
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-stone-100 to-amber-50 flex flex-col">
      {/* Nano Banana 标识 - 右上角 */}
      <div className="absolute top-4 right-4 z-20">
        <div className="text-xs text-slate-400 font-medium flex items-center gap-1">
          🍌 Generated by Nano Banana
        </div>
      </div>
      
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-300/15 to-amber-400/15 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-rose-300/15 to-orange-300/15 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 flex-1 p-6">
        {/* 标题区域 */}
        <div className="text-center mb-16">
          {/* GBase Logo */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex justify-center my-8"
          >
            <img 
              src="/logo.png" 
              alt="Gbase Logo" 
              className="w-[180px] object-contain"
            />
          </motion.div>
          
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
                         className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-stone-800 via-amber-800 to-orange-800 bg-clip-text text-transparent mb-6"
          >
            魔法のように、ワンクリックでデジタルヒューマンを生成✨
          </motion.h1>
          

        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* 左侧表单 */}
            <motion.div
              initial={{ x: -60, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
              className="relative"
            >
              {/* 装饰元素 */}
              <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg opacity-60"></div>
              <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg opacity-60"></div>
              
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 relative overflow-hidden">
                {/* 背景装饰 */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-100/50 to-orange-100/50 rounded-full -translate-y-16 translate-x-16"></div>
                
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  デジタルヒューマンを設定
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2 text-amber-600" />
                      デジタルヒューマンの名前
                      <span className="ml-1 text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        onInvalid={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('必須項目です。')}
                        onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('')}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-300 bg-white/50 backdrop-blur-sm text-slate-800 placeholder-slate-400 text-base"
                        placeholder="デジタルヒューマンの名前を入力"
                        required
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-500/0 to-orange-500/0 group-hover:from-amber-500/5 group-hover:to-orange-500/5 transition-all duration-300 pointer-events-none"></div>
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-emerald-600" />
                      スタイル選択
                      <span className="ml-1 text-slate-400 text-xs">(Optional)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['フォーマル', 'カジュアル', '活発', '外国風', 'スタイリッシュ', 'ファンタジー'].map((style) => (
                        <button
                          key={style}
                          type="button"
                          onClick={() => handleInputChange('purpose', formData.purpose === style ? '' : style)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all du
                              ration-200 border-2 ${
                            formData.purpose === style
                              ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                              : 'bg-white/50 border-slate-200 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {style}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-slate-600" />
                      会社のウェブサイトURL
                      <span className="ml-1 text-slate-400 text-xs">(Optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.companyUrl}
                        onChange={(e) => handleInputChange('companyUrl', e.target.value)}
                        onInvalid={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('正しいURLを入力してください')}
                        onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('')}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-slate-500/20 focus:border-slate-500 transition-all duration-300 bg-white/50 backdrop-blur-sm text-slate-800 placeholder-slate-400 text-base"
                        placeholder="https://yourcompany.com"
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-slate-500/0 to-stone-500/0 group-hover:from-slate-500/5 group-hover:to-stone-500/5 transition-all duration-300 pointer-events-none"></div>
                    </div>
                    <p className="text-sm text-slate-500 mt-2 ml-2">
                      会社の公式ウェブサイトを分析して、会社のデジタルヒューマンイメージを提供します
                    </p>
                  </div>

                  <div className="space-y-3">
                    {!showPreview ? (
                      <button
                        type="submit"
                        disabled={isAnalyzing}
                        className="w-full bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-amber-700 hover:via-orange-700 hover:to-red-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1 disabled:transform-none"
                      >
                        {isAnalyzing ? (
                          "分析中..."
                        ) : (
                          <>
                            生成を開始
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRegenerate}
                        disabled={isGenerating}
                        className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-amber-700 hover:to-orange-700 transition-all duration-300 flex items-center justify-center shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className={`w-5 h-5 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                        {isGenerating ? '再生成中...' : '再生成'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>

            {/* 右侧预览区域 */}
            <motion.div
              initial={{ x: 60, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
              className="relative"
            >
              {/* 装饰元素 */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg opacity-60"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg opacity-60"></div>

              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  {(() => {
                    const title = showPreview && generatedAvatar === 'generated' ? '生成されたデジタルヒューマン' : 'デジタルヒューマン(Preview)';
                    console.log(`Title render - showPreview: ${showPreview}, generatedAvatar: ${generatedAvatar}, title: ${title}`);
                    return title;
                  })()}
                </h2>

                {/* 双Preview布局 */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* 第一个Preview + 按钮 */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-stone-50 via-amber-50 to-orange-50 rounded-3xl relative overflow-hidden" style={{ height: '400px' }}>
                      {/* 背景装饰 */}
                      <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full -translate-y-12 -translate-x-12"></div>
                      <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-br from-rose-200/30 to-pink-200/30 rounded-full translate-y-8 translate-x-8"></div>

                      {/* 数字人预览 */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {showPreview ? (
                          <>
                            {!generatedAvatar ? (
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-3"></div>
                                <p className="text-gray-500 text-sm">準備中...</p>
                              </div>
                            ) : (
                              <div className="relative w-full h-full">
                                <Live2DAvatarGenerated
                                  ref={live2dRef}
                                  className="w-full h-full"
                                  scale={0.35}
                                  position={{ x: 0, y: -40 }}
                                  jsFile="/view1/anime_haru_generated.js"
                                  textureUrl={textureUrl1 || undefined}
                                  modelUrl={modelUrl1 || undefined}
                                  onLoad={() => {
                                    console.log('Live2D #1 loaded successfully');
                                    setLive2dLoaded(true);
                                    setLive2dError(null);
                                  }}
                                  onError={(error) => {
                                    console.error('Live2D #1 error:', error);
                                    setLive2dError(error);
                                    setLive2dLoaded(false);
                                  }}
                                />
                                {isGenerating && (
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="text-center bg-white/90 rounded-xl p-3">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mx-auto mb-2"></div>
                                      <p className="text-rose-600 text-xs font-medium">適用中...</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-stone-200 to-amber-200 rounded-full flex items-center justify-center">
                                <User className="w-8 h-8 text-stone-500" />
                              </div>
                              <p className="text-stone-500 text-xs">Preview 1</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 状态信息覆盖层 */}
                      {showPreview && generatedAvatar && !isGenerating && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white/90 to-transparent p-3">
                          <div className="space-y-2">
                            {!live2dLoaded && (
                              <p className="text-gray-600 text-xs text-center leading-relaxed">
                                読み込み中...
                              </p>
                            )}
                            {live2dError && (
                              <p className="text-xs text-red-600 text-center">
                                エラー
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 第一个窗口的按钮 */}
                    {showPreview && (
                      <button
                        onClick={() => handleUseAvatar(avatarUUID1, 1)}
                        disabled={!generatedAvatar || isSavingAvatar}
                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-xl font-semibold text-sm hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                      >
                        {isSavingAvatar ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            保存中...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            このアバターと会話
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* 第二个Preview + 按钮 */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-stone-50 via-amber-50 to-orange-50 rounded-3xl relative overflow-hidden" style={{ height: '400px' }}>
                      {/* 背景装饰 */}
                      <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full -translate-y-12 -translate-x-12"></div>
                      <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-br from-rose-200/30 to-pink-200/30 rounded-full translate-y-8 translate-x-8"></div>

                      {/* 数字人预览 */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {showPreview ? (
                          <>
                            {!generatedAvatar ? (
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-3"></div>
                                <p className="text-gray-500 text-sm">準備中...</p>
                              </div>
                            ) : (
                              <div className="relative w-full h-full">
                                <Live2DAvatarGenerated
                                  ref={live2dRef2}
                                  className="w-full h-full"
                                  scale={0.35}
                                  position={{ x: 0, y: -40 }}
                                  jsFile="/view2/anime_haru_generated.js"
                                  textureUrl={textureUrl2 || undefined}
                                  modelUrl={modelUrl2 || undefined}
                                  onLoad={() => {
                                    console.log('Live2D #2 loaded successfully');
                                  }}
                                  onError={(error) => {
                                    console.error('Live2D #2 error:', error);
                                  }}
                                />
                                {isGenerating && (
                                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                    <div className="text-center bg-white/90 rounded-xl p-3">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mx-auto mb-2"></div>
                                      <p className="text-rose-600 text-xs font-medium">適用中...</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-stone-200 to-amber-200 rounded-full flex items-center justify-center">
                                <User className="w-8 h-8 text-stone-500" />
                              </div>
                              <p className="text-stone-500 text-xs">Preview 2</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 状态信息覆盖层 */}
                      {showPreview && generatedAvatar && !isGenerating && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white/90 to-transparent p-3">
                          <div className="space-y-2">
                            {!live2dLoaded && (
                              <p className="text-gray-600 text-xs text-center leading-relaxed">
                                読み込み中...
                              </p>
                            )}
                            {live2dError && (
                              <p className="text-xs text-red-600 text-center">
                                エラー
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 第二个窗口的按钮 */}
                    {showPreview && (
                      <button
                        onClick={() => handleUseAvatar(avatarUUID2, 2)}
                        disabled={!generatedAvatar || isSavingAvatar}
                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-xl font-semibold text-sm hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                      >
                        {isSavingAvatar ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            保存中...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            このアバターと会話
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Powered by GBase 底部标识 */}
      <div className="relative z-10 py-4">
        <div className="text-center">
          <div className="text-sm text-slate-500 font-medium">
            Powered by <a 
              href="https://cs.gbase.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-amber-600 font-semibold hover:text-amber-700 transition-colors duration-200 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://cs.gbase.ai', '_blank', 'noopener,noreferrer');
              }}
            >
              GBase
            </a>
          </div>
        </div>
      </div>

      {/* 加载弹框 */}
      {isAnalyzing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent mb-4"></div>
            <p className="text-lg font-semibold text-gray-700">生成中...</p>
          </div>
        </div>
      )}

      {/* Avatar保存中弹框 */}
      {isSavingAvatar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mb-4"></div>
            <p className="text-lg font-semibold text-gray-700">データを保存中...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Page1;
